import { ReactElement, useEffect, useMemo, useState } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../../messages/en-US";
import { EventFilter } from "../../../../Events/Utilities/EventsApiService";
import { useQuery } from "react-query";
import { ReactQueryKeys } from "../../../../../Constants/ClientRoutingConstants";
import moment from "moment";
import siteExplorerApiService from "../../../Utilities/SiteExplorerApiService";
import LoadingSpinner from "../../../../Shared/Components/LoadingSpinner";
import ErrorCard from "../../../../Shared/Components/Cards/ErrorCard";
import NoDataPage from "../../NoDataPage";
import EndpointPressureEventsDto from "../../../../../Models/Endpoint/EndpointPressureEventsDto";
import EndpointStatusChart from "../EndpointStatusChart";
import useChartStore from "../../../../../Zustand/chartStore";
import { ChartToolbar } from "../../../Utilities/ChartToolbar";
import EndpointFlowRateEventsDto from "../../../../../Models/Endpoint/EndpointFlowRateEventsDto";
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import EndpointEventsTable from "../../../Components/Endpoints/EndpointEventsTable";
import EndpointPressureEventsTable from "../../../Components/Endpoints/EndpointPressureEventsTable";
import EndpointFlowRateEventsTable from "../../../Components/Endpoints/EndpointFlowRateEventsTable";
import EndpointGpsEventsTable from "../../../Components/Endpoints/EndpointGpsEventsTable";
import EndpointTemperatureEventsTable from "../../../Components/Endpoints/EndpointTemperatureEventsTable";
import EndpointGreasePumpEventsTable from "../../../Components/Endpoints/EndpointGreasePumpEventsTable";
import { StateEventsReadDto } from "../../../../../Models/State/StateEventsReadDto";
import EndpointPressureEventsTableDto from "../../../../../Models/Endpoint/EndpointPressureEventsTableDto";
import EndpointFlowRateEventsTableDto from "../../../../../Models/Endpoint/EndpointFlowRateEventsTableDto";
import EndpointGpsEventsTableDto from "../../../../../Models/Endpoint/EndpointGpsEventsTableDto";
import EndpointTemperatureEventsTableDto from "../../../../../Models/Endpoint/EndpointTemperatureEventsTableDto";
import EndpointFeatureDto from "../../../../../Models/Endpoint/EndpointFeatureDto";
import assetsDashboardApiService from "../../../../Dashboards/Assets/Utilities/AssetsDashboardApiService";
import LocoFlowMeterReadDto from "../../../../../Models/Dashboard/LocoFlowMeterReadDto";

export interface EndpointActivityTabPageProps {
  eventFilter: EventFilter
  selectedItem: string
  selectedItemSerialNo?: string;
  endpointFeature: any;
  showTables?: boolean;
  isSimpleChart?: boolean;
  showChartTools?: boolean;
  chartParentHeight?: number;
  aggregator?: string;
  machineryType?: string;
  greasePumpData?: {
    serialNo: string;
    channelNo: number;
    timestamps: number[];
    values: number[];
  };
  showLegendOverride?: boolean;
  showStatus?: boolean;
  /** When set (e.g. from dashboard), avoids refetch when switching tabs */
  queryStaleTime?: number;
  /** Called when loading finishes, reports whether chart data exists for the current filter */
  onHasData?: (hasData: boolean) => void;
}

// Internal component to render tables
function ActivityTablesView(props: {
  eventFilter: EventFilter;
  selectedItem: string;
  aggregator: string;
  endpointFeature: EndpointFeatureDto;
  machineryType?: string;
  selectedItemSerialNo?: string;
  showGreasePump?: boolean;
}) {
  const localizationService = useLocalization();
  const [selected, setSelected] = useState<number>(0);

  const handleSelect = (e: TabStripSelectEventArguments) => {
    setSelected(e.selected);
  };

  const greasePumpEventsTableQuery = useQuery<StateEventsReadDto[]>([`greasePumpEventsTableQuery${props.selectedItem}`, props.selectedItem, props.eventFilter.startDate, props.eventFilter.endDate], async () => {
    return assetsDashboardApiService.getEndpointEventsState(props.selectedItem, props.eventFilter.startDate!, props.eventFilter.endDate!);
  }, { enabled: !!props.showGreasePump && !!props.eventFilter.startDate && !!props.eventFilter.endDate });

  const greasePumpTab = props.showGreasePump ? (
    <TabStripTab title={localizationService.toLanguageString('custom.greasePump', enMessages.custom.greasePump)} >
      <EndpointGreasePumpEventsTable events={greasePumpEventsTableQuery.data ?? []} endpointSerialNo={props.selectedItemSerialNo} />
    </TabStripTab>
  ) : null;

  const endpointsQueriesTable = useQuery<any>(['endpointTable', props.selectedItem, props.eventFilter, props.aggregator], 
    async () => siteExplorerApiService.getAllEndpointsEventsFlowTable(props.eventFilter, props.selectedItem, props.aggregator), 
    {enabled: props.endpointFeature?.isFlowSupported} 
  );

  const endpointPressureEventsTableQuery = useQuery<EndpointPressureEventsTableDto[]>([`endpointPressureEventsTableQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, props.aggregator], async () => {
    return siteExplorerApiService.getEndpointPressureEventsTable(props.selectedItem, props.eventFilter, props.aggregator) 
  }, {enabled: props.endpointFeature?.isPressureSupported});

  // Query for Locomotives - use getLocoFlowMeter
  const locoFlowMeterQuery = useQuery<LocoFlowMeterReadDto[]>([`locoFlowMeterQuery${props.selectedItem}`, props.selectedItem, props.eventFilter.startDate, props.eventFilter.endDate], async () => {
    if (!props.eventFilter.startDate || !props.eventFilter.endDate) {
      throw new Error("Start date and end date are required");
    }
    return assetsDashboardApiService.getLocoFlowMeter(props.selectedItem, props.eventFilter.startDate, props.eventFilter.endDate);
  }, {enabled: props.machineryType === "Locomotive" && props.endpointFeature?.isFlowRateSupported && !!props.eventFilter.startDate && !!props.eventFilter.endDate});

  // Query for regular endpoints
  const endpointFlowRateEventsTableQuery = useQuery<EndpointFlowRateEventsTableDto[]>([`endpointFlowRateEventsTableQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, props.aggregator], async () => {
    return siteExplorerApiService.getEndpointFlowRateEventsTable(props.selectedItem, props.eventFilter, 'none') 
  }, {enabled: props.machineryType !== "Locomotive" && props.endpointFeature?.isFlowRateSupported});

  // Transform LocoFlowMeterReadDto to table format
  const transformedLocoFlowMeterData = useMemo(() => {
    if (props.machineryType === "Locomotive" && locoFlowMeterQuery.data && Array.isArray(locoFlowMeterQuery.data)) {
      return locoFlowMeterQuery.data.map(item => ({
        timestamp: item.timestamp,
        flowRate: item.flowRateTemperatureCompensated || item.flowRate || 0,
        volume: item.volumeTemperatureCompensated || item.volume || 0
      }));
    }
    return [];
  }, [locoFlowMeterQuery.data, props.machineryType]);

  const endpointGpsEventsTableQuery = useQuery<EndpointGpsEventsTableDto[]>([`endpointGpsEventsTableQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, props.aggregator], async () => {
    return siteExplorerApiService.getEndpointGpsEventsTable(props.selectedItem, props.eventFilter, 'none') 
  }, {enabled: props.endpointFeature?.isGpsSupported});

  const endpointTemperatureEventsTableQuery = useQuery<EndpointTemperatureEventsTableDto[]>([`endpointTemperatureEventsTableQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, props.aggregator], async () => {
    return siteExplorerApiService.getEndpointTemperatureEventsTable(props.selectedItem, props.eventFilter, 'none') 
  }, {enabled: props.endpointFeature?.isTemperatureSupported});

  if (endpointPressureEventsTableQuery.isLoading || endpointPressureEventsTableQuery.isFetching || 
      endpointsQueriesTable.isLoading || endpointsQueriesTable.isFetching || 
      endpointFlowRateEventsTableQuery.isLoading || endpointFlowRateEventsTableQuery.isFetching ||
      locoFlowMeterQuery.isLoading || locoFlowMeterQuery.isFetching ||
      endpointGpsEventsTableQuery.isLoading || endpointGpsEventsTableQuery.isFetching || 
      endpointTemperatureEventsTableQuery.isLoading || endpointTemperatureEventsTableQuery.isFetching ||
      (props.showGreasePump && (greasePumpEventsTableQuery.isLoading || greasePumpEventsTableQuery.isFetching))) {
    return <LoadingSpinner />;
  } else if (endpointPressureEventsTableQuery.isError || endpointsQueriesTable.isError || endpointFlowRateEventsTableQuery.isError || locoFlowMeterQuery.isError || endpointGpsEventsTableQuery.isError || endpointTemperatureEventsTableQuery.isError || greasePumpEventsTableQuery.isError) {
    return <ErrorCard />;
  } else {
    return (
      <div className="mt-5">
        {props.endpointFeature?.isPressureSupported ? 
          <TabStrip selected={selected} onSelect={handleSelect} className="h-full">
            <TabStripTab title={localizationService.toLanguageString('custom.flow', enMessages.custom.flow)} >
              <EndpointEventsTable events={endpointsQueriesTable.data!} aggregator={props.aggregator}/>
            </TabStripTab>
            <TabStripTab title={localizationService.toLanguageString('custom.pressure', enMessages.custom.pressure)} >
              <EndpointPressureEventsTable events={endpointPressureEventsTableQuery.data!} aggregator={props.aggregator}/>   
            </TabStripTab>
            <TabStripTab title={localizationService.toLanguageString('custom.temperature', enMessages.custom.temperature)} >
              <EndpointTemperatureEventsTable events={endpointTemperatureEventsTableQuery.data!} aggregator={'none'}/>
            </TabStripTab>
            {greasePumpTab}
          </TabStrip>
        :
        props.endpointFeature?.isFlowRateSupported ? (
          props.showGreasePump ? (
            <TabStrip selected={selected} onSelect={handleSelect} className="h-full">
              <TabStripTab title={localizationService.toLanguageString('custom.flowRate', enMessages.custom.flowRate)} >
                <EndpointFlowRateEventsTable
                  events={props.machineryType === "Locomotive" ? transformedLocoFlowMeterData : (endpointFlowRateEventsTableQuery.data || [])}
                  aggregator={'none'}
                  isSimplified={props.machineryType === "Locomotive"}
                  endpointSerialNo={props.selectedItemSerialNo}
                />
              </TabStripTab>
              {greasePumpTab}
            </TabStrip>
          ) : (
            // Show Flow Rate table directly without tab
            <EndpointFlowRateEventsTable
              events={props.machineryType === "Locomotive" ? transformedLocoFlowMeterData : (endpointFlowRateEventsTableQuery.data || [])}
              aggregator={'none'}
              isSimplified={props.machineryType === "Locomotive"}
              endpointSerialNo={props.selectedItemSerialNo}
            />
          )
        ) : props.endpointFeature?.isGpsSupported || props.endpointFeature?.isTemperatureSupported ? (
          <TabStrip selected={selected} onSelect={handleSelect}>
            {props.endpointFeature?.isFlowSupported &&
              <TabStripTab title={localizationService.toLanguageString('custom.flow', enMessages.custom.flow)} >
                <EndpointEventsTable events={endpointsQueriesTable.data!} aggregator={props.aggregator}/>
              </TabStripTab>
            }
            {(props.endpointFeature?.isGpsSupported && !!endpointGpsEventsTableQuery.data) &&
              <TabStripTab title={localizationService.toLanguageString('custom.gps', enMessages.custom.gps)} >
                <EndpointGpsEventsTable events={endpointGpsEventsTableQuery.data!} aggregator={'none'} title={localizationService.toLanguageString('custom.gps', enMessages.custom.gps)}/>   
              </TabStripTab>
            }
            {(props.endpointFeature?.isTemperatureSupported && !!endpointTemperatureEventsTableQuery.data) &&
              <TabStripTab title={localizationService.toLanguageString('custom.temperature', enMessages.custom.temperature)} >
                <EndpointTemperatureEventsTable events={endpointTemperatureEventsTableQuery.data!} aggregator={'none'}/>
              </TabStripTab>
            }
            {greasePumpTab}
          </TabStrip>
        )
        :
        props.showGreasePump ? (
          <TabStrip selected={selected} onSelect={handleSelect} className="h-full">
            <TabStripTab title={localizationService.toLanguageString('custom.flow', enMessages.custom.flow)} >
              <EndpointEventsTable events={endpointsQueriesTable.data!} aggregator={props.aggregator}/>
            </TabStripTab>
            {greasePumpTab}
          </TabStrip>
        ) : (
          <EndpointEventsTable events={endpointsQueriesTable.data!} aggregator={props.aggregator}/>
        )
        }
      </div>
    );
  }
}


export default function EndpointActivityTabPage(props: EndpointActivityTabPageProps): ReactElement {
  const localizationService = useLocalization();

  const storeAggregator = useChartStore((state) => state.aggregator);
  const aggregator = props.aggregator || storeAggregator;
  const viewType = useChartStore(state => state.viewType);
  const setViewType = useChartStore(state => state.setViewType);
  const setAggregator = useChartStore((state) => state.setAggregator);
  const setVisibleRange = useChartStore((state) => state.setVisibleRange);
  const setAutoRange = useChartStore((state) => state.setAutoRange);

  // Check if it's GPS only (special case)
  const isGpsOnly = props.endpointFeature?.isGpsSupported && 
                    !props.endpointFeature?.isBiDirectional && 
                    !props.endpointFeature?.isFlowRateSupported && 
                    !props.endpointFeature?.isFlowSupported && 
                    !props.endpointFeature?.isPressureSupported && 
                    !props.endpointFeature?.isTemperatureSupported;

  const queryOptions = props.queryStaleTime != null ? { staleTime: props.queryStaleTime } : {};

  // Query for GPS (always called, but can be disabled)
  const endpointGpsEventsTableQuery = useQuery<EndpointGpsEventsTableDto[]>([`endpointGpsEventsTableQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, aggregator], async () => {
    return siteExplorerApiService.getEndpointGpsEventsTable(props.selectedItem, props.eventFilter, 'none') 
  }, { ...queryOptions, enabled: isGpsOnly });

  const endpointsLastSeenQuery = useQuery<any>(
    [ReactQueryKeys.EndpointsLastSeenQuery, props.selectedItem, props.eventFilter, props.selectedItemSerialNo],
    async () => {
      return siteExplorerApiService.getEndpointsLastSeen(props.selectedItemSerialNo || '');
    },
    { ...queryOptions, enabled: !isGpsOnly && props.selectedItemSerialNo !== '' }
  );

  const endpointsQuery = useQuery<any>([`endpointChart${props.selectedItem}`, props.selectedItem, props.eventFilter, aggregator], async () => {
    return  siteExplorerApiService.getAllEndpointsEventsFlowChart(props.eventFilter, props.selectedItem, aggregator) 
  }, { ...queryOptions, enabled: !isGpsOnly });

  const lastHeartbeatUtc = useMemo(() => {
    if(isGpsOnly) return 0;
    // If mock flow rate data is provided, use its last timestamp
    if(props.selectedItemSerialNo !== '') {
      return moment(endpointsLastSeenQuery.data?.lastHeartbeatUtc).unix()
    } else {
        if(endpointsQuery.data?.timestamps.length === 0) {
          return 0
        }else {
          return endpointsQuery.data?.timestamps[endpointsQuery.data?.timestamps.length - 1]
        }
    }
  }, [endpointsLastSeenQuery.data, endpointsQuery.data, isGpsOnly, props.selectedItemSerialNo])

  const lastHeartbeatUtcFormatted = lastHeartbeatUtc === 0 ? localizationService.toLanguageString('custom.noEventsToDetermine', enMessages.custom.noEventsToDetermine) : aggregator === 'daily' && props.selectedItemSerialNo === '' ? moment.unix(lastHeartbeatUtc).format('DD/MM/YYYY') : moment.unix(lastHeartbeatUtc).format('DD/MM/YYYY @ HH:mm:ss')

  const endpointPressureEventsChartQuery = useQuery<EndpointPressureEventsDto>([`endpointPressureEventsChartQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, aggregator], async () => {
    return  siteExplorerApiService.getEndpointPressureEventsChart(props.selectedItem, props.eventFilter, aggregator) 
  }, { ...queryOptions, enabled: !isGpsOnly && props.endpointFeature?.isPressureSupported });

  // Query for Locomotives - use getLocoFlowMeterChartData
  const locoFlowMeterChartDataQuery = useQuery<any>([`locoFlowMeterChartDataQuery${props.selectedItem}`, props.selectedItem, props.eventFilter.startDate, props.eventFilter.endDate], async () => {
    if (!props.eventFilter.startDate || !props.eventFilter.endDate) {
      throw new Error("Start date and end date are required");
    }
    return assetsDashboardApiService.getLocoFlowMeterChartData(props.selectedItem, props.eventFilter.startDate, props.eventFilter.endDate);
  }, { ...queryOptions, enabled: props.machineryType === "Locomotive" && !isGpsOnly && props.endpointFeature?.isFlowRateSupported && !!props.eventFilter.startDate && !!props.eventFilter.endDate });

  // Query for regular endpoints
  const endpointFlowRateEventsChartQuery = useQuery<EndpointFlowRateEventsDto | any>([`endpointFlowRateEventsChartQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, aggregator], async () => {
    return  siteExplorerApiService.getEndpointFlowRateEventsChart(props.selectedItem, props.eventFilter, 'none') 
  }, {
    ...queryOptions,
    enabled: props.machineryType !== "Locomotive" && !isGpsOnly && props.endpointFeature?.isFlowRateSupported
  });

  const endpointTemperatureEventsChartQuery = useQuery<any>([`endpointTemperatureEventsChartQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, aggregator], async () => {
    return  siteExplorerApiService.getEndpointTemperatureEventsChart(props.selectedItem, props.eventFilter, 'none') 
  }, { ...queryOptions, enabled: !isGpsOnly && props.endpointFeature?.isTemperatureSupported });

  const notificationsQuery = useQuery<any>(
    ["notificationsStatus", props.selectedItem, props.eventFilter],
    async () => {
      return siteExplorerApiService.getEndpointNotifications(props.selectedItem, props.eventFilter, 1);
    },
    { ...queryOptions, enabled: !isGpsOnly }
  );

  const statusQuery = useQuery<any>(
    ["statusChart", props.selectedItem, props.eventFilter],
    async () => {
      return siteExplorerApiService.getEndpointStatus(props.selectedItem, props.eventFilter, 0);
    },
    { ...queryOptions, enabled: !isGpsOnly && !!props.selectedItem }
  );

  useEffect(() => {
    if (!isGpsOnly && props.isSimpleChart) {
      setViewType('bar');
      setAggregator('none');
      setVisibleRange(moment(props.eventFilter.startDate).unix(), moment(props.eventFilter.endDate).unix());
      setAutoRange(true);
    }
  }, [props.isSimpleChart, setViewType, setAggregator, props.eventFilter, props.selectedItem, setVisibleRange, setAutoRange, isGpsOnly]);


  useEffect(() => {
    if(!isGpsOnly && viewType !== 'userBar' && viewType !== 'userLine' && viewType !== 'table' && viewType !== 'bar') {
      setViewType('bar');
    }
  }, [viewType, setViewType, isGpsOnly]);

  useEffect(() => {
    if (!props.onHasData || isGpsOnly) return;

    const isAnyQueryLoading =
      endpointsQuery.isLoading || endpointsQuery.isFetching ||
      endpointPressureEventsChartQuery.isLoading ||
      endpointFlowRateEventsChartQuery.isLoading || endpointFlowRateEventsChartQuery.isFetching ||
      locoFlowMeterChartDataQuery.isLoading || locoFlowMeterChartDataQuery.isFetching;

    if (isAnyQueryLoading) {
      props.onHasData(true);
      return;
    }

    const localHasFlowRateChartData = props.machineryType === "Locomotive"
      ? !!(locoFlowMeterChartDataQuery?.data && (
          (Array.isArray(locoFlowMeterChartDataQuery.data.timestamps) && locoFlowMeterChartDataQuery.data.timestamps.length > 0) ||
          (Array.isArray(locoFlowMeterChartDataQuery.data.volumes) && locoFlowMeterChartDataQuery.data.volumes.length > 0) ||
          (Array.isArray(locoFlowMeterChartDataQuery.data.volumesTemperatureCompensated) && locoFlowMeterChartDataQuery.data.volumesTemperatureCompensated.length > 0) ||
          (Array.isArray(locoFlowMeterChartDataQuery.data.flowRates) && locoFlowMeterChartDataQuery.data.flowRates.length > 0) ||
          (Array.isArray(locoFlowMeterChartDataQuery.data.flowRatesTemperatureCompensated) && locoFlowMeterChartDataQuery.data.flowRatesTemperatureCompensated.length > 0)
        ))
      : !!(endpointFlowRateEventsChartQuery?.data?.timestamps && endpointFlowRateEventsChartQuery.data.timestamps.length > 0);

    const localHasPressureChartPoints =
      !!props.endpointFeature?.isPressureSupported &&
      (endpointPressureEventsChartQuery.data?.timestamps?.length ?? 0) > 0;

    const hasData =
      (endpointsQuery.data !== undefined && (endpointsQuery.data?.timestamps?.length ?? 0) !== 0) ||
      localHasFlowRateChartData ||
      endpointFlowRateEventsChartQuery.data !== undefined ||
      localHasPressureChartPoints;

    props.onHasData(hasData);
  }, 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    props.onHasData,
    isGpsOnly,
    endpointsQuery.isLoading,
    endpointsQuery.isFetching,
    endpointsQuery.data,
    endpointPressureEventsChartQuery.isLoading,
    endpointPressureEventsChartQuery.data,
    endpointFlowRateEventsChartQuery.isLoading,
    endpointFlowRateEventsChartQuery.isFetching,
    endpointFlowRateEventsChartQuery.data,
    locoFlowMeterChartDataQuery.isLoading,
    locoFlowMeterChartDataQuery.isFetching,
    locoFlowMeterChartDataQuery.data,
    props.machineryType,
    props.endpointFeature,
  ]);

  const chartData = useMemo(() => {
    if (isGpsOnly) return null;


    if (endpointPressureEventsChartQuery?.data?.timestamps && 
        endpointPressureEventsChartQuery.data.timestamps.length > 0 && 
        props.endpointFeature?.isPressureSupported)
    {
      const pDto = endpointPressureEventsChartQuery.data;
      const flowTs = endpointsQuery.data?.timestamps ?? [];
      const flowVol = endpointsQuery.data?.volumeDispensedData ?? [];
      const chartTs = flowTs.length > 0 ? flowTs : (pDto.timestamps ?? []);
      return {
        statusDataChart: {
          dateFilter: props.eventFilter,
          timestamps: chartTs,
          statusData:statusQuery.data,
          alerts:notificationsQuery.data
        },
        activityPressureDataChart: {
          dateFilter: props.eventFilter,
          timestamps: flowTs,
          volumeDispensedData: flowVol,
          strokeThickness: 1, 
          lastHeartbeatUtc:lastHeartbeatUtc,
          lastHeartbeatUtcFormatted:lastHeartbeatUtcFormatted,
          pressureEvents: pDto,
          temperatureData: endpointTemperatureEventsChartQuery.data,
          lastSyncUtc: moment(endpointsLastSeenQuery.data?.lastSyncUtc).unix()
        }
      }
    }
    else if (
        ((props.machineryType === "Locomotive" && locoFlowMeterChartDataQuery?.data) ||
        (endpointFlowRateEventsChartQuery?.data?.timestamps && 
        endpointFlowRateEventsChartQuery.data.timestamps.length > 0)) && 
        props.endpointFeature?.isFlowRateSupported
    )
    {
      // For Locomotives, use getLocoFlowMeterChartData
      if (props.machineryType === "Locomotive" && locoFlowMeterChartDataQuery?.data) {
        const chartData = locoFlowMeterChartDataQuery.data;
        
        // Ensure arrays are valid
        const timestamps = Array.isArray(chartData.timestamps) ? chartData.timestamps : [];
        const volumes = Array.isArray(chartData.volumesTemperatureCompensated) 
          ? chartData.volumesTemperatureCompensated 
          : (Array.isArray(chartData.volumes) ? chartData.volumes : []);
        const flowRates = Array.isArray(chartData.flowRatesTemperatureCompensated) 
          ? chartData.flowRatesTemperatureCompensated 
          : (Array.isArray(chartData.flowRates) ? chartData.flowRates : []);
        
        // Check if we have any data at all
        if (timestamps.length === 0 && volumes.length === 0 && flowRates.length === 0) {
          return null;
        }
        
        // If timestamps is empty but we have volumes or flowRates, create timestamps from dateFilter
        const finalTimestamps = timestamps.length > 0 ? timestamps : 
          (volumes.length > 0 || flowRates.length > 0 ? 
            Array.from({ length: Math.max(volumes.length, flowRates.length) }, (_, i) => 
              moment(props.eventFilter.startDate).add(i, 'hours').unix()
            ) : []);
        
        return {
          statusDataChart: {dateFilter: props.eventFilter, timestamps: finalTimestamps, statusData:statusQuery.data, alerts:notificationsQuery.data},
          activityFlowRateDataChart: {
            dateFilter: props.eventFilter,
            timestamps: finalTimestamps,
            volume: volumes,
            flowRate: flowRates,
            strokeThickness: 1,
            lastHeartbeatUtc: lastHeartbeatUtc,
            lastHeartbeatUtcFormatted: lastHeartbeatUtcFormatted
          }
        }
      }
      
      // For regular endpoints, use query data
      const flowRateData = endpointFlowRateEventsChartQuery?.data;
      
      if (!flowRateData || !flowRateData.timestamps || flowRateData.timestamps.length === 0) {
        return null;
      }
      
      const flowRateTimestamps = flowRateData.timestamps;
      const minFlowRate = flowRateData.minFlowRate || [];
      const maxFlowRate = flowRateData.maxFlowRate || [];
      const avgFlowRate = flowRateData.avgFlowRate || [];
      const volume = flowRateData.volume || [];
      
      const dataLength = flowRateTimestamps.length;
      const safeMinFlowRate = minFlowRate.length === dataLength ? minFlowRate : new Array(dataLength).fill(0);
      const safeMaxFlowRate = maxFlowRate.length === dataLength ? maxFlowRate : new Array(dataLength).fill(0);
      const safeAvgFlowRate = avgFlowRate.length === dataLength ? avgFlowRate : new Array(dataLength).fill(0);
      const safeVolume = volume.length === dataLength ? volume : new Array(dataLength).fill(0);
      
      return {
        statusDataChart: {dateFilter: props.eventFilter, timestamps: flowRateTimestamps, statusData:statusQuery.data, alerts:notificationsQuery.data},
        activityFlowRateDataChart: {
          dateFilter: props.eventFilter,
          timestamps: flowRateTimestamps,
          minFlowRate: safeMinFlowRate,
          maxFlowRate: safeMaxFlowRate,
          avgFlowRate: safeAvgFlowRate,
          volume: safeVolume,
          strokeThickness: 1,
          lastHeartbeatUtc: lastHeartbeatUtc,
          lastHeartbeatUtcFormatted: lastHeartbeatUtcFormatted,
          temperatureData: endpointTemperatureEventsChartQuery.data,
          lastSyncUtc: endpointsLastSeenQuery.data?.lastSyncUtc ? moment(endpointsLastSeenQuery.data.lastSyncUtc).unix() : 0
        }
      }
    } else
    {
      return {
        statusDataChart: {dateFilter: props.eventFilter, timestamps: endpointsQuery.data?.timestamps, statusData:statusQuery.data, alerts:notificationsQuery.data},
        activityDataChart: {dateFilter: props.eventFilter, timestamps: endpointsQuery.data?.timestamps, volumeDispensedData:endpointsQuery.data?.volumeDispensedData, strokeThickness: 1, lastHeartbeatUtc:lastHeartbeatUtc ,lastHeartbeatUtcFormatted:lastHeartbeatUtcFormatted, lastSyncUtc: moment(endpointsLastSeenQuery.data?.lastSyncUtc).unix()}
      }
  }
  }, [endpointsQuery.data, endpointsLastSeenQuery.data, notificationsQuery.data, statusQuery.data, endpointPressureEventsChartQuery.data, endpointFlowRateEventsChartQuery.data, locoFlowMeterChartDataQuery.data, isGpsOnly, props.eventFilter, props.endpointFeature, props.machineryType, lastHeartbeatUtc, lastHeartbeatUtcFormatted, endpointTemperatureEventsChartQuery.data])

  // If GPS only, render simplified component
  if (isGpsOnly) {
    if (endpointGpsEventsTableQuery.isLoading || endpointGpsEventsTableQuery.isFetching) {
      return <div className="flex items-center justify-center h-full w-full">
        <LoadingSpinner />
      </div>;
    } else if (endpointGpsEventsTableQuery.isError) {
      return <ErrorCard />;
    } else {
      return (
        <div className="mt-5">
          <EndpointGpsEventsTable events={endpointGpsEventsTableQuery.data!} aggregator={'none'} title={localizationService.toLanguageString('custom.gpsEvents', enMessages.custom.gpsEvents)}/>   
        </div>
      );
    }
  }

  // Check if we have flow rate data (for Locomotives or regular endpoints)
  const hasFlowRateChartData = props.machineryType === "Locomotive" 
    ? (locoFlowMeterChartDataQuery?.data && (
        (Array.isArray(locoFlowMeterChartDataQuery.data.timestamps) && locoFlowMeterChartDataQuery.data.timestamps.length > 0) ||
        (Array.isArray(locoFlowMeterChartDataQuery.data.volumes) && locoFlowMeterChartDataQuery.data.volumes.length > 0) ||
        (Array.isArray(locoFlowMeterChartDataQuery.data.volumesTemperatureCompensated) && locoFlowMeterChartDataQuery.data.volumesTemperatureCompensated.length > 0) ||
        (Array.isArray(locoFlowMeterChartDataQuery.data.flowRates) && locoFlowMeterChartDataQuery.data.flowRates.length > 0) ||
        (Array.isArray(locoFlowMeterChartDataQuery.data.flowRatesTemperatureCompensated) && locoFlowMeterChartDataQuery.data.flowRatesTemperatureCompensated.length > 0)
      ))
    : (endpointFlowRateEventsChartQuery?.data?.timestamps && endpointFlowRateEventsChartQuery.data.timestamps.length > 0);

  const hasPressureChartPoints =
    !!props.endpointFeature?.isPressureSupported &&
    (endpointPressureEventsChartQuery.data?.timestamps?.length ?? 0) > 0;

  if (endpointsQuery.isLoading || endpointsQuery.isFetching || endpointsLastSeenQuery.isLoading || endpointPressureEventsChartQuery.isLoading || notificationsQuery.isLoading || notificationsQuery.isFetching || statusQuery.isLoading || statusQuery.isFetching || endpointFlowRateEventsChartQuery.isLoading || endpointFlowRateEventsChartQuery.isFetching || locoFlowMeterChartDataQuery.isLoading || locoFlowMeterChartDataQuery.isFetching || endpointTemperatureEventsChartQuery.isLoading || endpointTemperatureEventsChartQuery.isFetching) {
    return <div className="flex items-center justify-center h-full w-full">
      <LoadingSpinner />
    </div>;
  } else if (endpointsQuery.isError || endpointsLastSeenQuery.isError || endpointPressureEventsChartQuery.isError || notificationsQuery.isError || statusQuery.isError || endpointFlowRateEventsChartQuery.isError || locoFlowMeterChartDataQuery.isError || endpointTemperatureEventsChartQuery.isError) {
    return <ErrorCard />;
  } else {
  return (
    <div className="text-center">
    {(endpointsQuery.data !== undefined && endpointsQuery.data?.timestamps?.length !== 0) || hasFlowRateChartData || (endpointFlowRateEventsChartQuery.data !== undefined) || hasPressureChartPoints ? (
      hasFlowRateChartData || (endpointFlowRateEventsChartQuery.data?.timestamps?.length !== 0) || hasPressureChartPoints ?  
      <div className="text-center">
        {!props.isSimpleChart && props.showChartTools !== false && (
          <ChartToolbar 
            title={localizationService.toLanguageString('custom.events', enMessages.custom.events)}
            lastSeen={null}
            showAutoRange={true} 
            showAggregatorOptions={!props.endpointFeature?.isFlowRateSupported} 
            showChartOptions={true}
            showThresholds={false} 
            showSingleMultipleChart={props.endpointFeature?.isPressureSupported}
            showTable={true}
          />
        )}
        {viewType !== 'table' && chartData && (
          endpointPressureEventsChartQuery?.data?.timestamps && 
          endpointPressureEventsChartQuery.data.timestamps.length > 0 && 
          props.endpointFeature?.isPressureSupported ? (
            <EndpointStatusChart 
              chartType="activity-pressure"
              statusChartData={chartData.statusDataChart} 
              activityPressureChartData={chartData.activityPressureDataChart} 
              isSimpleChart={props.isSimpleChart} 
              chartParentHeight={props.chartParentHeight}
              greasePumpData={props.greasePumpData}
              showLegendOverride={props.showLegendOverride}
              showStatus={props.showStatus}
            />
          ) : hasFlowRateChartData && 
             props.endpointFeature?.isFlowRateSupported ? (
            <EndpointStatusChart 
              chartType="activity-flow-rate"
              statusChartData={chartData.statusDataChart} 
              activityFlowRateChartData={chartData.activityFlowRateDataChart} 
              isSimpleChart={props.isSimpleChart}
              chartParentHeight={props.chartParentHeight}
              greasePumpData={props.greasePumpData}
              isFullscreen={!props.isSimpleChart}
              showLegendOverride={props.showLegendOverride}
              showStatus={props.showStatus}
            />
          ) : (
            <EndpointStatusChart 
              chartType="activity"
              statusChartData={chartData.statusDataChart} 
              activityChartData={chartData.activityDataChart} 
              isSimpleChart={props.isSimpleChart}
              chartParentHeight={props.chartParentHeight}
              greasePumpData={props.greasePumpData}
              showLegendOverride={props.showLegendOverride}
              showStatus={props.showStatus}
            />
          )
        )}
        {viewType === 'table' && <ActivityTablesView aggregator={aggregator} eventFilter={props.eventFilter} selectedItem={props.selectedItem} endpointFeature={props.endpointFeature} machineryType={props.machineryType} selectedItemSerialNo={props.selectedItemSerialNo} showGreasePump={!!props.greasePumpData}/>}
      </div> :
       <ActivityTablesView aggregator={aggregator} eventFilter={props.eventFilter} selectedItem={props.selectedItem} endpointFeature={props.endpointFeature} machineryType={props.machineryType} selectedItemSerialNo={props.selectedItemSerialNo} showGreasePump={!!props.greasePumpData}/>
    ) : (
      <div className="text-center">
        <NoDataPage/>
      </div>
    )}
  </div>
    
  )
}}
